/**
 * Shared reasoning helpers — used by every opportunity card (signals,
 * AI Pulse dashboard, landing, public share view) so the "why this
 * matters" + "our take" copy is consistent everywhere.
 */

export const TONE_STYLES = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", label: "🚀 Ship now" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-900",  label: "✅ Build the MVP" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   label: "🔬 Validate first" },
  orange:  { bg: "bg-orange-50",  border: "border-orange-200",  text: "text-orange-900",  label: "🥊 Differentiate first" },
  zinc:    { bg: "bg-zinc-50",    border: "border-zinc-200",    text: "text-zinc-700",    label: "👀 Watch" },
  rose:    { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-900",    label: "⚠️ Crowded — proceed cautiously" },
};

// Keywords in the AI analysis text that signal a crowded / saturated market.
// Order matters here — we check the most unambiguous phrases first.
const SATURATION_KEYWORDS = [
  "saturated", "crowded with", "established players",
  "many tools", "many solutions", "many competitors",
  "incumbents", "highly competitive", "very competitive",
];

// Named tools that almost always mean the space is occupied by paid players.
// Detection is fuzzy — we just check substring presence in the reasoning text.
const KNOWN_INCUMBENTS = [
  "jasper", "copy.ai", "copyai", "writesonic", "anyword", "rytr",
  "calendly", "motion", "reclaim.ai", "clockwise",
  "buffer", "hootsuite", "later", "sprout social",
  "notion", "airtable", "monday.com", "asana", "clickup",
  "intercom", "drift", "zendesk", "freshdesk", "helpscout",
  "mailchimp", "convertkit", "klaviyo", "activecampaign",
  "predis.ai", "lately",
];

// Negation phrases that should cancel a saturation hit when they appear
// within a short distance BEFORE the keyword.
const NEGATIONS = ["no ", "not ", "without ", "isn't ", "aren't ", "no real "];

function detectCompetition(opp) {
  const text = `${opp?.reasoning || ""} ${opp?.existing_solutions || ""} ${opp?.gap || ""}`.toLowerCase();
  const hits = [];

  for (const kw of SATURATION_KEYWORDS) {
    const idx = text.indexOf(kw);
    if (idx === -1) continue;
    // Check 25-char window before the keyword for negation
    const before = text.slice(Math.max(0, idx - 25), idx);
    if (NEGATIONS.some((n) => before.includes(n))) continue;
    hits.push(kw);
    break;
  }

  const incumbents = KNOWN_INCUMBENTS.filter((name) => text.includes(name));

  // Backend's competition_score: 100 = no competition, 0 = max competition.
  // We treat <= 45 as "crowded" since it's the lower tercile.
  const compScore = typeof opp?.competition_score === "number" ? opp.competition_score : null;
  const competitionScoreCrowded = compScore !== null && compScore <= 45;

  return {
    saturated: hits.length > 0,
    incumbents,
    competitionScoreCrowded,
    isCrowded: hits.length > 0 || incumbents.length >= 2 || competitionScoreCrowded,
    isVeryCrowded: (hits.length > 0 && incumbents.length >= 2) || (hits.length > 0 && competitionScoreCrowded),
  };
}

export function buildOpportunityReasoning(opp) {
  const sb = opp.source_breakdown || {};
  const demand   = (sb.reddit || 0) + (sb.hackernews || 0) + (sb.indiehackers || 0);
  const paid     = (sb.fiverr || 0) + (sb.freelancer || 0) + (sb.producthunt || 0);
  const builders = (sb.github_trending || 0) + (sb.producthunt || 0);
  const score    = Math.round(opp.weighted_total || 0);
  const comp     = detectCompetition(opp);

  const lines = [];
  if (demand >= 20) {
    lines.push({ icon: "💬", text: `${demand} public complaints — strong, repeated demand signal (not a one-off rant).` });
  } else if (demand > 0) {
    lines.push({ icon: "💬", text: `${demand} early discussion${demand > 1 ? "s" : ""} surfaced — niche but the conversation is starting.` });
  }
  if (paid >= 5) {
    lines.push({ icon: "💰", text: `${paid} paid transactions found — the market is already opening its wallet for adjacent solutions.` });
  } else if (paid > 0) {
    lines.push({ icon: "💰", text: `${paid} paid signal${paid > 1 ? "s" : ""} of validation — proof someone will pay, even if the volume is small.` });
  }
  if (builders > 0) {
    lines.push({ icon: "🛠️", text: `${builders} builder${builders > 1 ? "s are" : " is"} actively shipping in this space — the tech is feasible RIGHT NOW.` });
  }
  if ((opp.cross_source_confidence || 0) >= 3) {
    lines.push({ icon: "🎯", text: `Validated across ${opp.cross_source_confidence} independent sources — drastically reduces "is this just one loud user?" risk.` });
  }
  // Competition warning surfaces inside "Why this matters" so the user
  // sees the trade-off BEFORE the verdict line.
  if (comp.isVeryCrowded) {
    const named = comp.incumbents.slice(0, 3).map((s) => s.replace(/\b\w/g, (c) => c.toUpperCase())).join(", ");
    lines.push({ icon: "⚠️", text: `Crowded market — established players${named ? ` (${named}${comp.incumbents.length > 3 ? ", …" : ""})` : ""} already serve this. Differentiation is mandatory.` });
  } else if (comp.isCrowded) {
    const named = comp.incumbents.slice(0, 2).map((s) => s.replace(/\b\w/g, (c) => c.toUpperCase())).join(", ");
    lines.push({ icon: "⚔️", text: `Competition exists${named ? ` (${named}${comp.incumbents.length > 2 ? ", and others" : ""})` : ""} — you'll need a clear wedge to win share.` });
  }

  // ---------- Verdict ----------
  // Honest verdict: high score alone is NOT enough if the market is crowded.
  // We downgrade to "Validate first" / "Differentiate first" instead of cheering "BUILD".
  let action = "", tone = "violet";

  if (comp.isVeryCrowded) {
    if (score >= 70) {
      action = `Strong signal but market is saturated — DO NOT build a me-too. Pick one underserved sub-niche or a 10× cheaper angle, prove differentiation with 100 landing-page signups first.`;
      tone = "rose";
    } else {
      action = `Crowded market with mediocre signal. Skip unless you have an unfair advantage (existing audience, proprietary data, deep niche expertise).`;
      tone = "zinc";
    }
  } else if (comp.isCrowded) {
    if (score >= 80 || (paid >= 5 && demand >= 20)) {
      action = `Demand is strong but competition is real — find your differentiation wedge FIRST (sub-niche, price, UX, integration). Build the MVP only after a 3-day landing-page test with 50+ signups.`;
      tone = "orange";
    } else if (score >= 65) {
      action = `Validate before building — competitors exist, so you must prove that buyers will switch. Talk to 5 paying users of the incumbents to find the gap.`;
      tone = "amber";
    } else {
      action = `Watch — crowded space + thin signal. Re-check next scan cycle for momentum.`;
      tone = "zinc";
    }
  } else if (score >= 85 || (paid >= 5 && demand >= 20)) {
    action = `Ship now — demand × paid validation × builder momentum all aligned, AND we found no dominant incumbents. First-mover window is wide open.`;
    tone = "emerald";
  } else if (score >= 70 || (paid >= 2 && demand >= 10)) {
    action = `Worth a 5-day MVP — signals strong enough to warrant a landing page + paid traffic test. We didn't detect saturated competition.`;
    tone = "violet";
  } else if (demand >= 10) {
    action = `Validate further — demand is real but monetization path unclear. Talk to 5 of the complainers before building.`;
    tone = "amber";
  } else {
    action = `Watch — too early or too thin. Re-check next scan cycle for momentum shifts.`;
    tone = "zinc";
  }

  return { lines, action, tone, demand, paid, builders, competition: comp };
}

export const SOURCE_META = {
  reddit:          { label: "Reddit",       color: "#FF4500" },
  hackernews:      { label: "HN",           color: "#FF6600" },
  fiverr:          { label: "Fiverr",       color: "#1DBF73" },
  freelancer:      { label: "Freelancer",   color: "#29B2FE" },
  indiehackers:    { label: "IndieHackers", color: "#0E2439" },
  producthunt:     { label: "ProductHunt",  color: "#DA552F" },
  github_trending: { label: "GitHub",       color: "#24292E" },
};

// ============================================================
// Freshness — drives "🆕 New today" / "🔥 Fresh this week" badges
// ============================================================
export function getFreshness(opp) {
  const ts = opp?.scored_date || opp?.created_date;
  if (!ts) return null;
  const ageMs = Date.now() - new Date(ts).getTime();
  const hours = ageMs / 3_600_000;
  if (hours < 0) return null;
  if (hours <= 24) return "new_today";
  if (hours <= 24 * 7) return "fresh_week";
  return null;
}

export function formatNextScanCountdown(nextIso) {
  if (!nextIso) return "";
  const diff = new Date(nextIso).getTime() - Date.now();
  if (diff <= 0) return "any moment now";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ============================================================
// Competition Gate — data-grounded verdict that OVERRIDES the optimistic
// "BUILD THE MVP" recommendation. The backend writes `opp.competition_gate`.
// ============================================================
export const COMPETITION_STYLES = {
  green:  { badge: "bg-emerald-500", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900" },
  yellow: { badge: "bg-amber-500",   bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900" },
  red:    { badge: "bg-rose-600",    bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-900" },
  grey:   { badge: "bg-zinc-400",    bg: "bg-zinc-100",   border: "border-zinc-300",    text: "text-zinc-700" },
};

export function getCompetitionVerdict(opp) {
  const g = opp?.competition_gate;
  if (!g || !g.verdict) return null;
  return {
    verdict: g.verdict,                              // GREEN | YELLOW | RED | UNVERIFIED
    color: g.color || "grey",                        // green | yellow | red | grey
    label: g.label || "",
    competitors: Array.isArray(g.competitors) ? g.competitors : [],
    establishedCount: g.established_count || 0,
    searchStatus: g.search_status || "ok",           // ok | failed | empty
    downgraded: !!g.downgraded,
    saasCheckIncomplete: !!g.saas_check_incomplete,
    originalScore: typeof g.original_score === "number" ? g.original_score : null,
    fromCache: !!g.from_cache,
  };
}

export function formatStars(n) {
  if (!n || n <= 0) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
}

// Plain-English, data-derived explanation of WHY a card got its verdict.
// Everything is computed from the same grounded competition_gate evidence the
// chips render from — no fabrication, no extra backend call.
const STAR_THRESHOLD = 1000;

export function getCompetitionReason(v) {
  if (!v || !v.verdict) return null;
  const comps = Array.isArray(v.competitors) ? v.competitors : [];

  if (v.verdict === "UNVERIFIED" || v.searchStatus !== "ok") {
    return "We couldn't complete the competition check (search failed or returned nothing), so this verdict isn't trustworthy yet.";
  }

  if (v.verdict === "RED") {
    // A single dominant open-source project is enough to mark RED.
    const bigRepo = comps
      .filter((c) => (c.stars || 0) > STAR_THRESHOLD)
      .sort((a, b) => (b.stars || 0) - (a.stars || 0))[0];
    if (bigRepo) {
      return `A major open-source project already owns this space — ${bigRepo.name} has ${formatStars(bigRepo.stars)}★ on GitHub. Hard to out-build.`;
    }
    const robust = comps.filter((c) => c.robust);
    const n = robust.length || v.establishedCount || comps.length;
    return `Found ${n} established competitor${n === 1 ? "" : "s"} already serving this market — it's crowded, so skip unless you have a sharp angle.`;
  }

  if (v.verdict === "YELLOW") {
    const n = v.establishedCount || comps.filter((c) => c.established).length;
    if (n >= 1) {
      return `Found ${n} real competitor${n === 1 ? "" : "s"} here — only worth building if you have a wedge they don't cover.`;
    }
    return "Weak demand signal and no clear competitors yet — talk to ~10 real users before you build anything.";
  }

  if (v.verdict === "GREEN") {
    return "No established competitors found — this looks like an open lane. Validate that people actually want it before building.";
  }
  return null;
}
